// components
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

// hooks
import { useState } from 'react';

// types
import type { SlicedTableLoading, SlicedTableProps } from './SlicedTable.types';

// styles
import {
  StyledBreakdownTableRow,
  StyledOpenRequestsTableCell,
  StyledOpenRequestsTableContainer
} from '../../OpenRequests.styles';

// utils
import classNames from 'classnames';
import { format, isSaturday, isSunday, isToday } from 'date-fns';
import { formatByLocale } from 'utils/date';
import { getRowKey } from '../../OpenRequests.utils';

const SlicedTable = ({
  headerDays,
  rows,
  start,
  end,
  openRoster
}: SlicedTableProps) => {
  const [loading, setLoading] = useState<SlicedTableLoading | null>(null);

  const onOpenRoster = (rowKey: string, id: number, date: Date) => {
    openRoster(id, date, true);
    setLoading({ rowKey, date });
  };

  return (
    <StyledOpenRequestsTableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {headerDays.slice(start, end).map(({ date }) => (
              <StyledOpenRequestsTableCell
                key={date.toISOString()}
                align="center"
                className={classNames({
                  'is-today': isToday(date),
                  'is-saturday': isSaturday(date),
                  'is-sunday': isSunday(date)
                })}
              >
                <div>
                  <b>{formatByLocale(date, 'EEEEEE')}</b>
                </div>
                <div className="date-label">{format(date, 'dd')}</div>
              </StyledOpenRequestsTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const Row =
              row.type === 'breakdown' ? StyledBreakdownTableRow : TableRow;
            const rowKey = getRowKey(row);

            return (
              <Row
                key={rowKey}
                className={classNames({
                  'is-striped': row.striped,
                  'is-breakdown': row.type === 'breakdown'
                })}
              >
                {row.days.slice(start, end).map(({ date, count }) => (
                  <TableCell
                    key={date.toISOString()}
                    align="center"
                    className={classNames({
                      'is-today': isToday(date),
                      'is-saturday': isSaturday(date),
                      'is-sunday': isSunday(date)
                    })}
                  >
                    {count > 0 ? (
                      <Chip
                        variant={
                          row.type === 'breakdown' ? 'outlined' : 'filled'
                        }
                        size={row.type === 'breakdown' ? 'small' : 'medium'}
                        onClick={() =>
                          onOpenRoster(rowKey, row.costCenterId, date)
                        }
                        label={
                          loading &&
                          loading.rowKey === rowKey &&
                          loading.date === date ? (
                            <i className="icon-spinner icon-spin" />
                          ) : (
                            count
                          )
                        }
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                ))}
              </Row>
            );
          })}
        </TableBody>
      </Table>
    </StyledOpenRequestsTableContainer>
  );
};

export default SlicedTable;
